import { useMemo } from 'react';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';

// ============= Component =============
export const useFirstPartnerSchema = (
  formState: FormState,
): FormSchemaConfig => {
  // const firstPartnerData = formState.data?.first_partner;

  return useMemo<FormSchemaConfig>(() => {
    const isEditAllowed = Array.isArray(formState.data?.userAssignedSections)
      ? (formState.data?.userAssignedSections as string[]).includes(
          'first_partner',
        )
      : false;
    // Safely access the responsible lawyer data
    // const firstPartner = firstPartnerData?.[0]?.label || firstPartnerData || '';

    // Create the main section that matches EnhancedGroupedSection type
    const firstPartnerSection: EnhancedGroupedSection = {
      // profile: {
      //   name: firstPartner,
      //   variant: 'row',
      //   theme: 'blue',
      //   job: 'First Partner',
      // },
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Authorisation - 1st Partner',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'first_approver',
              label: 'Do you wish to authorise this payment?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether you authorize this payment'
                  : undefined,
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'first_reject_details',
              label: 'Rejection Details',
              required: true,
              validate: (value) =>
                !value ? 'Please enter the rejection details' : undefined,
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'first_verify_details',
              label:
                'I have reviewed the relevant elements of the underlying transaction or other documentation and confirm they are consistent with the payment request details (e.g. number of payments, values to be paid, beneficiary names, reason for payment):',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.first_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the documentation'
                  : undefined,
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'first_verify_bank',
              label:
                'I am satisfied that the bank details have been verified and appropriately confirmed by the Responsible Lawyer',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.first_approver === 'yes' && !value
                  ? 'Please verify the bank details'
                  : undefined,
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'first_verify_currency',
              label: 'I have confirmed the transaction currency:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.first_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the bank details'
                  : undefined,
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'first_verify_reason',
              required: true,
              validate: (value, formData) =>
                formData?.first_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the reason for the payment'
                  : undefined,
              label:
                'As an approving partner I have sufficient understanding of the reason for the payment:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled:
                    formState.completedSections?.includes('first_partner') ||
                    !isEditAllowed,
                },
              ],
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'textArea',
              name: 'first_comments',
              label: 'Additional Comments',
              disabled:
                formState.completedSections?.includes('first_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'first_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [firstPartnerSection],
      dependencies: {
        showWhen: [
          {
            field: 'payment_details',
          },
        ],
      },
    };
  }, [formState]);
};
